import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';

import { SafetyRiskAlert } from '~/lib/types/survey/SafetyRiskAlert';
import { useAppDispatch, useAppSelector } from '~/store/hooks';

interface SafetyAlertsSliceState {
  queue: SafetyRiskAlert[];
  seen: { [safetyRiskAlertId: string]: true };
}

const initialState: SafetyAlertsSliceState = {
  queue: [],
  seen: {},
};

const enqueueIfUnseen = (
  state: SafetyAlertsSliceState,
  alert: SafetyRiskAlert,
) => {
  if (state.seen[alert.safetyRiskAlertId]) {
    return;
  }
  state.seen[alert.safetyRiskAlertId] = true;
  state.queue.push(alert);
};

const safetyAlertsSlice = createSlice({
  initialState,
  name: 'safetyAlerts',
  reducers: {
    alertsHydrated: (state, action: PayloadAction<SafetyRiskAlert[]>) => {
      [...action.payload]
        .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))
        .forEach((alert) => enqueueIfUnseen(state, alert));
    },
    alertReceived: (state, action: PayloadAction<SafetyRiskAlert>) => {
      enqueueIfUnseen(state, action.payload);
    },
    alertViewed: (state, action: PayloadAction<string>) => {
      const alert = state.queue.find(
        (a) => a.safetyRiskAlertId === action.payload,
      );
      if (alert) {
        alert.statusCode = 'VIEWED';
      }
    },
    alertViewFailed: (state, action: PayloadAction<string>) => {
      const alert = state.queue.find(
        (a) => a.safetyRiskAlertId === action.payload,
      );
      if (alert) {
        alert.statusCode = 'CREATED';
      }
    },
    alertAcknowledged: (state, action: PayloadAction<string>) => {
      state.seen[action.payload] = true;
      state.queue = state.queue.filter(
        (alert) => alert.safetyRiskAlertId !== action.payload,
      );
    },
  },
});

export const {
  alertsHydrated: alertsHydratedAction,
  alertReceived: alertReceivedAction,
  alertViewed: alertViewedAction,
  alertViewFailed: alertViewFailedAction,
  alertAcknowledged: alertAcknowledgedAction,
} = safetyAlertsSlice.actions;

export default safetyAlertsSlice.reducer;

export const useActiveSafetyAlert = (): SafetyRiskAlert | null =>
  useAppSelector((state) => state.safetyAlerts.queue[0] ?? null);

export const useSafetyAlertsQueueLength = (): number =>
  useAppSelector((state) => state.safetyAlerts.queue.length);

export const useSafetyAlertsDispatch = () => {
  const dispatch = useAppDispatch();

  const alertsHydrated = useCallback(
    (alerts: SafetyRiskAlert[]) => dispatch(alertsHydratedAction(alerts)),
    [dispatch],
  );
  const alertReceived = useCallback(
    (alert: SafetyRiskAlert) => dispatch(alertReceivedAction(alert)),
    [dispatch],
  );
  const alertViewed = useCallback(
    (safetyRiskAlertId: string) =>
      dispatch(alertViewedAction(safetyRiskAlertId)),
    [dispatch],
  );
  const alertViewFailed = useCallback(
    (safetyRiskAlertId: string) =>
      dispatch(alertViewFailedAction(safetyRiskAlertId)),
    [dispatch],
  );
  const alertAcknowledged = useCallback(
    (safetyRiskAlertId: string) =>
      dispatch(alertAcknowledgedAction(safetyRiskAlertId)),
    [dispatch],
  );

  return useMemo(
    () => ({ alertsHydrated, alertReceived, alertViewed, alertViewFailed, alertAcknowledged }),
    [alertsHydrated, alertReceived, alertViewed, alertViewFailed, alertAcknowledged],
  );
};
